import {useCallback, useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {AccessibilityInfo, Keyboard, Platform} from 'react-native';
import * as Keychain from 'react-native-keychain';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {handleLoginFormErrorMessage} from '/component/screen/Login/LoginForm/LoginForm.util';
import {LOGIN_TYPE} from '/enum/biometrics.enum';
import {
  HAS_OPTED_INTO_BIOMETRICS,
  HAS_SETUP_PIN,
  HAS_SUCCESSFULLY_LOGGED_IN,
} from '/enum/storage.enum';
import {EVENT_NAMES, LOGIN_ENABLE} from '/enum/tealium.enum';
import {useStorage} from '/util/async-storage/useStorage';
import {getBiometricsUtilDefaultInstance} from '/util/biometrics/biometrics.util';
import {Logger} from '/util/splunk/logger.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {handleBiometricsModalPopup, handleError} from './useLoginForm.utils';

export const useLoginForm = ({
  onSignIn,
  onBiometrics,
  muleTokenBiometric,
  onResetPassword,
  defaultEmailValue,
  showPopup,
  hidePopup,
  lastPaidRank,
}: any) => {
  const {
    setValue,
    watch,
    handleSubmit,
    register,
    formState: {errors},
    clearErrors: clearFieldErrors,
  } = useForm({
    defaultValues: {login: defaultEmailValue, password: ''},
  });
  const {
    setValue: setFirstSuccessfulLoginValue,
    getValue: getFirstSuccessfulLoginValue,
  } = useStorage({
    key: HAS_SUCCESSFULLY_LOGGED_IN,
    defaultValue: false,
  });
  const loginErrorMutable = useRef('');

  const {setValue: setBiometricsOptInValue, getValue: getBiometricsOptInValue} =
    useStorage({
      key: HAS_OPTED_INTO_BIOMETRICS,
      defaultValue: false,
    });

  const {setValue: setPinOptInValue, getValue: getPinOptInValue} = useStorage({
    key: HAS_SETUP_PIN,
    defaultValue: false,
  });
  const {setValue: setInitBiometricsAndPin} = useStorage({
    key: HAS_OPTED_INTO_BIOMETRICS,
    defaultValue: false,
  });

  const [error, setError] = useState(null);
  const [biometricType, setBiometricType] = useState();

  useEffect(() => {
    register('login', {required: true});
    register('password', {required: true});
    getBiometricsUtilDefaultInstance().getBiometricType({setBiometricType});
  }, [register]);

  const registerForBiometricOption = async (trackLoginType: string) => {
    if (trackLoginType != LOGIN_TYPE.manual) {
      register('login', {required: false});
      register('password', {required: false});
    }
  };
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.loginClick,
  });

  const getRefreshToken = async (): Promise<string | null> => {
    try {
      const expiryKey = 'refreshTokenExpiry';

      // 1. Get expiry from AsyncStorage
      const expiry = await AsyncStorage.getItem(expiryKey);

      // 2. Validate expiry
      if (!expiry || Date.now() > Number(expiry)) {
        // Expired — clear expiry and Keychain
        await AsyncStorage.removeItem(expiryKey);
        await Keychain.resetGenericPassword({
          service: 'com.yourApp.refreshToken',
        });
        return null;
      }

      // 3. Get token from Keychain
      const credentials = await Keychain.getGenericPassword({
        service: 'com.yourApp.refreshToken',
      });

      if (credentials && credentials.username === 'password') {
        return credentials.password;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const getMuleRefreshToken = async (service: string) => {
    try {
      const credentials = await Keychain.getGenericPassword({
        service: service,
      });

      if (credentials) {
        return credentials.password;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const onSubmit = useCallback(
    async (
      data: any,
      trackLoginType: string,
      setUpFaceIDval,
      setBiometricsOptIn,
      setValue,
      buttonData,
      biometricsOptIn,
    ) => {
      Keyboard.dismiss();
      try {
        let refreshToken;
        let muleToken;

        if (trackLoginType === LOGIN_TYPE.manual) {
          await onSignIn(data.login.trim(), data.password);
          Logger.manualSplunkLog({
            loginType: 'Manual',
            message: `Manual Logging with user : ${data.login}`,
            severity: 'LOW',
          });
        } else {
          refreshToken = await getRefreshToken();
          muleToken = await getMuleRefreshToken('com.yourApp.refreshMuleToken');
          if (!refreshToken) {
            Logger.manualSplunkLog({
              loginType: 'biometrics',
              message:
                'Skipped biometric login: refresh token is expired or missing',
              severity: 'LOW',
            });
            return;
          }

          try {
            Logger.manualSplunkLog({
              loginType: 'biometrics',
              message: `Biometrics/PIN Logging with ${refreshToken} and mule ${muleToken}`,
              severity: 'LOW',
            });
            await onBiometrics(data.login.trim(), refreshToken);
            await muleTokenBiometric(muleToken);
          } catch (biometricError) {
            Logger.manualSplunkLog({
              loginType: 'Error',
              message: `Calling Refresh token API ${refreshToken} and mule ${muleToken} and error it ${biometricError}`,
              severity: 'LOW',
            });
          }
        }
        refreshToken = await getRefreshToken();
        muleToken = await getMuleRefreshToken('com.yourApp.refreshMuleToken');
        if (!refreshToken) {
          Logger.manualSplunkLog({
            loginType: 'biometrics',
            message:
              'Skipped biometric login: refresh token is expired or missing',
            severity: 'LOW',
          });
          return;
        }
        const tokens = {muleToken, refreshToken};

        await getBiometricsUtilDefaultInstance().setPasswordBiometric(
          data.login,
          tokens,
          LOGIN_TYPE.biometrics,
        );

        await getBiometricsUtilDefaultInstance().setPasswordPIN(
          data.login,
          tokens,
        );

        const firstLoginValue = await getFirstSuccessfulLoginValue();
        const enablePopup = trackLoginType === 'biometrics';
        const biometricsOptedIn = Platform.OS === 'ios' ? enablePopup : false;
        const login = data.login;
        const password = data.password;
        // const firstLoginValue = await getFirstSuccessfulLoginValue();
        // const biometricsOptedIn = await getBiometricsOptInValue();
        // const pinOptedIn = await getPinOptInValue();

        handleBiometricsModalPopup({
          biometricsOptedIn,
          firstLoginValue,
          biometricType,
          showPopup,
          hidePopup,
          setBiometricsOptInValue,
          setUpFaceIDval,
          handleOnSubmit,
          setBiometricsOptIn,
          login,
          password,
          setValue,
          buttonData,
          setBiometricType,
          biometricsOptIn,
        });
        getBiometricsUtilDefaultInstance().trackFirstLogin({
          setFirstSuccessfulLoginValue,
          getFirstSuccessfulLoginValue,
        })();
      } catch (err) {
        loginErrorMutable.current = 'Invalid credentails';
        const errorMessage = handleError({
          error: err,
          sendEvent,
          setError,
          email: data.login,
          login_enable: LOGIN_ENABLE.false,
          lastPaidRank,
        });

        AccessibilityInfo.announceForAccessibility(errorMessage);
      }
    },
    [
      onSignIn,
      onBiometrics,
      muleTokenBiometric,
      onResetPassword,
      getFirstSuccessfulLoginValue,
      getBiometricsOptInValue,
      getPinOptInValue,
      biometricType,
      setFirstSuccessfulLoginValue,
      setBiometricsOptInValue,
      setPinOptInValue,
      setInitBiometricsAndPin,
      sendEvent,
    ],
  );

  const onFormError = (props: any) => {
    const errorMessage = handleLoginFormErrorMessage({fieldErrors: props});
    setTimeout(() => {
      AccessibilityInfo.announceForAccessibility(errorMessage);
    }, 1000);
  };

  const handleOnSubmit = useCallback(
    async (
      trackLoginType: string,
      setUpFaceIDval,
      setValue,
      buttonData,
      setBiometricsOptIn,
      biometricsOptIn,
    ) => {
      await registerForBiometricOption(trackLoginType);
      handleSubmit(
        data =>
          onSubmit(
            data,
            trackLoginType,
            setUpFaceIDval,
            setValue,
            buttonData,
            setBiometricsOptIn,
            biometricsOptIn,
          ),
        onFormError,
      )();
    },
    [handleSubmit, onSubmit],
  );

  const clearErrors = useCallback(() => {
    clearFieldErrors();
    setError(null);
  }, [clearFieldErrors]);

  return {
    setValue,
    handleOnSubmit,
    formError: error,
    fieldErrors: errors,
    loginErrorMutable,
    clearErrors,
    values: {
      login: watch('login'),
      password: watch('password'),
    },
  };
};
